import React, { useEffect, useState } from 'react';

import { Backdrop, CircularProgress, Container, makeStyles } from '@material-ui/core';
import { useSnackbar } from 'notistack';
import TagManager from 'react-gtm-module';
import { useHistory, useLocation } from 'react-router-dom';
import './App.css';

import Footer from './components/footer/Footer';
import DialogContainer from './containers/dialog/Dialog';
import DrawerContainer from './containers/drawer/Drawer';
import Routes from './containers/routes/Routes';
import container from './setupAwilix';
import useBlocRef from './utils/useBlocRef.hook';

const tagManagerArgs = {
  gtmId: 'GTM-NBT6VLS',
};

TagManager.initialize(tagManagerArgs);

const isTokenPath = (pathname) => {
  if (pathname.startsWith('/viewer')) return true;
  if (pathname.startsWith('/report-viewer')) return true;

  return pathname === '/report-viewer' || pathname === '/viewer' || pathname.startsWith('/download/');
};

const useStyles = makeStyles((theme) => ({
  backdrop: {
    backgroundColor: '#7c7c7d',
    color: '#fff',
    zIndex: theme.zIndex.drawer + 1,
  },
}));

function App() {
  const classes = useStyles();
  const appBlocRef = useBlocRef('appBloc');
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const [initialized, setInitialized] = useState(false);
  const [auth, setAuth] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showFooter, setShowFooter] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    document.onmousemove = () => appBlocRef.current.resetTimer();
    document.onkeyup = () => appBlocRef.current.resetTimer();

    const initSubscription = appBlocRef.current.initialized$.subscribe(setInitialized);
    const authSubscription = appBlocRef.current.auth$.subscribe(setAuth);
    const isLoadingSubscription = appBlocRef.current.isAppLoading$.subscribe(setIsLoading);
    const inactivitySubscription = appBlocRef.current.inActivityLogout$.subscribe((logout) => {
      if (logout && !isTokenPath(pathname)) {
        appBlocRef.current.logout();
        history.push('/inactivity');
      }
    });

    if (!isTokenPath(pathname)) {
      appBlocRef.current.startInactivityCheck();
    }

    return () => {
      isLoadingSubscription.unsubscribe();
      initSubscription.unsubscribe();
      authSubscription.unsubscribe();
      inactivitySubscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    setShowFooter(auth && auth.role === 'patient');
  }, [auth]);

  useEffect(() => {
    const errorSubscription = appBlocRef.current.error$.subscribe((error) => {
      if (error && error.message && error.severity) {
        enqueueSnackbar(error.message, { autoHideDuration: 11000, variant: error.severity });
      }
    });

    // Check for encrypted token in URL before normal initialization for recorder
    const handleTokenAuth = async () => {
      const tokenAuthService = container.resolve('tokenAuthService');
      const authToken = tokenAuthService.getTokenFromUrl();

      if (authToken) {
        try {
          setIsLoading(true);

          const { accessToken, user } = await tokenAuthService.validateEncryptedToken(authToken);

          localStorage.setItem('accessToken', accessToken);
          localStorage.setItem('workspaceId', user.workspaceId);
          localStorage.setItem('lastUpTime', new Date().toISOString());

          tokenAuthService.cleanTokenFromUrl();

          // Initialize app and get profile
          await appBlocRef.current.init(false);

          history.push(pathname);
        } catch (error) {
          console.error('Token authentication failed:', error);
          enqueueSnackbar('Invalid or expired authentication token', { variant: 'error' });

          // Clean token from URL and proceed with normal initialization
          tokenAuthService.cleanTokenFromUrl();

          const shouldSkipAuth = isTokenPath(pathname);

          appBlocRef.current.init(shouldSkipAuth);
        } finally {
          setIsLoading(false);
        }
      } else {
        appBlocRef.current.init(isTokenPath(pathname));
      }
    };

    handleTokenAuth();

    return () => {
      errorSubscription.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enqueueSnackbar, history, pathname]);

  if (!initialized) {
    return <div />;
  }

  const loader = document.getElementById('html-loader');

  if (loader) {
    loader.style.display = 'none';
  }

  const minHeight = pathname === '/viewer' ? null : '95vh';

  return (
    <div className="App">
      <main>
        <Backdrop className={classes.backdrop} open={isLoading}>
          <CircularProgress color="inherit" />
        </Backdrop>

        <Container maxWidth={false} disableGutters style={{ minHeight }}>
          <Routes />
        </Container>
        {showFooter && <Footer />}
      </main>
      <DrawerContainer />
      <DialogContainer />
    </div>
  );
}

export default App;
